import { BATTERY_LOW_THRESHOLD_DEFAULT_VALUE } from '../../../location-card.constants';
import { alarmToggleActions, disableField, enableField } from '../../actions';
import { Section } from '../../models';
import { CommonSectionIds } from '../common.models';
import { ConfigureAlarmsFieldId } from '../common.models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { InitialLocationCardState } from './model';
import { field } from '../../core/field-schema';
import { required, pattern } from '../../core/validator-builders';
import { AlarmDevice, TritonMonitorConfiguration } from '../triton/models';
import { NonSensorDeviceNames } from 'app/shared/enums/device-type-code';

export const getValidators = function (isMetric: boolean) {
    if (isMetric) {
        return [
            pattern({
                value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            pattern({
                value: REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

/**
 * Triton Alarms Configuration Section
 */
export const configureAlarms = (state: InitialLocationCardState): Section => {
    const settings = state.settings;
    const config = state.monitorConfiguration as TritonMonitorConfiguration;

    const alarmData = config ? config.devices.find((v) => v.name === NonSensorDeviceNames.Alarm1) as AlarmDevice : null;

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];
    const batteryLowValidators = [
        required(),
        pattern({
            value: '(^\\d*(\\.\\d{0,1})?$)',
        }),
    ];

    return {
        info: {
            id: CommonSectionIds.ConfigureAlarms,
            title: 'ADD_EDIT.ADD_EDIT_CONFIGURE',
            displayTitle: true,
            expandable: true,
            expanded: false,
        },
        rows: [
            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: alarmData ? alarmData.lowLevelEnable : false,
                        panelClass: 'alarm-slide-toggle',
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? alarmData.lowLevelThreshold : null,
                        disabled: alarmData ? !alarmData.lowLevelEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Full Pipe Alarm Row
            {
                info: {
                    id: 'full-pipe-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.FullPipeEnable, 'fullPipeEnable', {
                        placeholder: 'COMMON.FULL_PIPE_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.fullPipeEnable : false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold })
                                    : disableField({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold }),
                            ],
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.FullPipeThreshold, 'fullPipethreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_FULL_PIPE',
                        readonly: true,
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? alarmData.fullPipeThreshold : null,
                        disabled: alarmData ? !alarmData.fullPipeEnable : true,
                        validators: getValidators(settings.isMetric),
                    }),
                ],
            },

            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.highLevelEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? alarmData.highLevelThreshold : null,
                        disabled: alarmData ? !alarmData.highLevelEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.highHighEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? alarmData.highHighThreshold : null,
                        disabled: alarmData ? !alarmData.highHighEnable : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Overflow Alarm Row - no inputs to enable/disable
            {
                info: {
                    id: 'overflow-alarm-row',
                    panelClass: 'each-configure-alarm margin-t-b-15',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.OverflowEnable, 'overflowEnable', {
                        placeholder: 'COMMON.OVERFLOW_ALARMS',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.overflowEnable : false,
                    }),
                ],
            },

            // Battery Low Alarm Row
            {
                info: {
                    id: 'battery-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ConfigureAlarmsFieldId.BatteryLowEnable, 'batteryLowEnable', {
                        placeholder: 'COMMON.BATTERY_LOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: alarmData ? alarmData.batteryLowEnable : false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ConfigureAlarmsFieldId.BatteryLowThreshold, batteryLowValidators),
                        },
                    }),
                    field.number(ConfigureAlarmsFieldId.BatteryLowThreshold, 'batteryLowThreshold', {
                        placeholder: 'COMMON.BATTERY_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: settings.voltUnit,
                        panelClass: 'alarm-input',
                        value: alarmData ? alarmData.batteryLowThreshold : BATTERY_LOW_THRESHOLD_DEFAULT_VALUE,
                        disabled: alarmData ? !alarmData.batteryLowEnable : true,
                        validators: batteryLowValidators,
                    }),
                ],
            },
        ],
    };
};
